import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * CurrencyContext — phase 1 multi-currency DISPLAY layer.
 *
 * Resolution priority:
 *   1. Explicit manual override (localStorage "am_display_ccy")
 *   2. Logged-in user's account override (user.display_currency)
 *   3. Logged-in user's country default (Nigeria → NGN, UK → GBP, …)
 *   4. IP geolocation guess (ipapi.co — free, no key)
 *   5. USD fallback
 *
 * Provides convert(amount, fromCcy) and formatPrice(amount, fromCcy, opts)
 * helpers that any component can use without thinking about FX rates.
 */
const CurrencyContext = createContext(null);

const STORAGE_KEY = "am_display_ccy";

// Minimal ISO-2 → country name map for the country.is fallback.
// Covers the diaspora hubs + every African country we showcase.
const ISO2_TO_NAME = {
  GB: "United Kingdom", US: "United States", CA: "Canada", IE: "Ireland",
  AU: "Australia", NZ: "New Zealand", DE: "Germany", FR: "France",
  ES: "Spain", IT: "Italy", NL: "Netherlands", SE: "Sweden", NO: "Norway",
  NG: "Nigeria", GH: "Ghana", KE: "Kenya", ZA: "South Africa", SN: "Senegal",
  ET: "Ethiopia", EG: "Egypt", TZ: "Tanzania", CM: "Cameroon", CI: "Côte d'Ivoire",
  MA: "Morocco", UG: "Uganda", ZW: "Zimbabwe", RW: "Rwanda",
};

// Browser language fallback — last resort when both IP services fail.
// e.g. "en-GB" → "United Kingdom", "fr-CA" → "Canada".
const localeCountryName = () => {
  try {
    const tag = navigator.language || (navigator.languages && navigator.languages[0]);
    if (!tag || tag.indexOf("-") < 0) return null;
    const cc = tag.split("-")[1].toUpperCase();
    return ISO2_TO_NAME[cc] || null;
  } catch { return null; }
};

const fallbackSupported = {
  GBP: { symbol: "£", name: "British Pound", decimals: 2 },
  USD: { symbol: "$", name: "US Dollar", decimals: 2 },
  EUR: { symbol: "€", name: "Euro", decimals: 2 },
  CAD: { symbol: "C$", name: "Canadian Dollar", decimals: 2 },
  NGN: { symbol: "₦", name: "Nigerian Naira", decimals: 0 },
};

export const CurrencyProvider = ({ children }) => {
  const { user } = useAuth() || {};
  const [rates, setRates] = useState(null);          // { USD:1, GBP:0.79, NGN:1620, … }
  const [supported, setSupported] = useState(fallbackSupported);
  const [countryDefaults, setCountryDefaults] = useState({});
  const [ipCurrency, setIpCurrency] = useState(null);
  const [ipCountry, setIpCountry] = useState(null); // pretty country name from ipapi (guests)
  const [override, setOverride] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });

  // Fetch rates + catalogue once per session
  useEffect(() => {
    let alive = true;
    api.get("/fx/rates")
      .then((r) => {
        if (!alive) return;
        setRates(r.data?.rates || null);
        if (r.data?.supported) setSupported(r.data.supported);
        if (r.data?.country_defaults) setCountryDefaults(r.data.country_defaults);
      })
      .catch(() => {/* offline ok — fallback path below */});
    return () => { alive = false; };
  }, []);

  // IP-based guess for guests (skip if user is logged in — we use their profile)
  useEffect(() => {
    if (user || override) return;
    let alive = true;
    // Backend proxy `/api/geo/ip-detect` — server-side fetches ipapi.co +
    // api.country.is so we never trigger a browser CORS preflight (which
    // used to flood the console with ~60 errors per page load).
    api.get("/geo/ip-detect")
      .then((r) => {
        if (!alive) return;
        const d = r?.data || {};
        const ccy = d.currency || null;
        if (ccy && supported[ccy]) setIpCurrency(ccy);
        if (d.country_name) {
          setIpCountry(d.country_name);
        } else if (d.country_iso2 && ISO2_TO_NAME[d.country_iso2]) {
          setIpCountry(ISO2_TO_NAME[d.country_iso2]);
        }
      })
      .catch(() => {/* silent — fall through to locale + USD */});
    return () => { alive = false; };
  }, [user, override, supported]);

  // Resolve the effective currency
  const displayCurrency = useMemo(() => {
    if (override && supported[override]) return override;
    if (user?.display_currency && supported[user.display_currency]) return user.display_currency;
    if (user?.country && supported) {
      // Look up country default — case-insensitive, handles short aliases (UK/USA) and ISO-2 codes (NG/GB)
      const c = user.country;
      let ccy = countryDefaults[c];
      if (!ccy) {
        const key = Object.keys(countryDefaults).find(k => k.toLowerCase() === c.toLowerCase());
        if (key) ccy = countryDefaults[key];
      }
      if (ccy && supported[ccy]) return ccy;
    }
    if (ipCurrency && supported[ipCurrency]) return ipCurrency;
    return "USD";
  }, [override, user, countryDefaults, ipCurrency, supported]);

  // Persist manual override + sync to backend if logged in
  const setCurrency = useCallback((code) => {
    if (code && !supported[code]) return;
    setOverride(code || null);
    try {
      if (code) localStorage.setItem(STORAGE_KEY, code); else localStorage.removeItem(STORAGE_KEY);
    } catch {/* ignore */}
    if (user) {
      api.post("/me/currency", { currency: code || "" }).catch(() => {/* non-blocking */});
    }
  }, [supported, user]);

  // Convert amount from a source currency to the displayCurrency.
  // Returns null if rates haven't loaded yet (callers should fall back to
  // showing the raw price).
  const convert = useCallback((amount, fromCcy) => {
    if (!rates || amount == null) return null;
    const from = (fromCcy || "USD").toUpperCase();
    const to = displayCurrency;
    if (from === to) return Number(amount);
    const rFrom = rates[from];
    const rTo = rates[to];
    if (!rFrom || !rTo) return null;
    // Base is USD; rates[X] = how many X per 1 USD
    const usd = Number(amount) / rFrom;
    const out = usd * rTo;
    // Apply same 1.5% markup as backend
    return out * 1.015;
  }, [rates, displayCurrency]);

  // Pretty-format a number with the right symbol + decimals.
  const formatPrice = useCallback((amount, fromCcy, opts = {}) => {
    const targetCcy = opts.in || displayCurrency;
    const meta = supported[targetCcy] || { symbol: targetCcy + " ", decimals: 2 };
    const decimals = meta.decimals ?? 2;
    let value;
    if (!fromCcy || fromCcy.toUpperCase() === targetCcy) {
      value = Number(amount);
    } else {
      const converted = convert(amount, fromCcy);
      if (converted == null) return null;
      value = converted;
    }
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return `${meta.symbol}${formatted}`;
  }, [convert, displayCurrency, supported]);

  // Dual-currency format: returns `{primary, original, isConverted, originalCurrency}`
  // so cards can show "$48.99 · listed £39.99" without each component re-computing
  // the conversion. `primary` is the converted local price (or original if same).
  const formatPriceDual = useCallback((amount, fromCcy) => {
    const original = (fromCcy || "").toUpperCase();
    const primary = formatPrice(amount, fromCcy);
    const isConverted = !!original && original !== displayCurrency && !!rates;
    let originalFmt = null;
    if (isConverted) {
      // Force-format in the listing's original currency.
      originalFmt = formatPrice(amount, fromCcy, { in: original });
    }
    return { primary, original: originalFmt, isConverted, originalCurrency: original };
  }, [formatPrice, displayCurrency, rates]);

  // Resolve the buyer's country — used by "Ships to your country" badges, regional CTAs etc.
  // Priority: logged-in user.country > IP geolocation country_name > navigator.language fallback.
  const userCountry = useMemo(
    () => user?.country || ipCountry || localeCountryName() || null,
    [user, ipCountry],
  );

  const value = useMemo(() => ({
    rates, supported, displayCurrency, userCountry,
    setCurrency, convert, formatPrice, formatPriceDual,
    isConverted: (fromCcy) => !!fromCcy && fromCcy.toUpperCase() !== displayCurrency && !!rates,
  }), [rates, supported, displayCurrency, userCountry, setCurrency, convert, formatPrice, formatPriceDual]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Allow components to render before the provider mounts during dev hot-reload.
    return {
      rates: null, supported: fallbackSupported, displayCurrency: "USD", userCountry: null,
      setCurrency: () => {}, convert: () => null,
      formatPrice: (a, c) => `${c || ""}${a}`,
      formatPriceDual: (a, c) => ({ primary: `${c || ""}${a}`, original: null, isConverted: false, originalCurrency: c }),
      isConverted: () => false,
    };
  }
  return ctx;
};
